/**
 * Etapa 3 — Prontuário (#366, §3.3 e §6): seis linhas fixas (os cinco documentos do item 20.6.1 e o
 * simulado), cada uma com o estado que o servidor calculou, o último registro e as ações que cabem.
 * Não é tabela: as linhas são fixas e o vazio é inalcançável. Sem `canEdit`, a etapa só mostra e baixa.
 */
import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useState } from 'react';
import { Alert, Box, Button, Typography } from '@mui/material';
import { ConfirmDialog } from '../../../../../components/common/ConfirmDialog';
import { flammableInstallationService } from '../../../../../services/flammableInstallationService';
import {
  FLAMMABLE_RECORD_TYPES,
  ROTULO_DO_TIPO_DE_REGISTRO,
  type FlammableItemSummary,
  type FlammableRecord,
  type FlammableRecordType,
} from '../../../../../types/flammableInstallation';
import { formatarData } from '../../flammableDates';
import { mensagemDoServidor } from '../../mensagensDeErro';
import type { ListStepProps, ResultadoDoEnvio, StepHandle } from '../FlammableInstallationWizard.types';
import { motivoDeRecusaDoArquivo, salvarComoDownload } from './arquivoDoRegistro';
import { FormularioDoDocumento, FormularioDoSimulado } from './FormularioDoRegistro';
import { LinhaDoProntuario } from './LinhaDoProntuario';
import {
  REGISTRO_VAZIO,
  registroParaEnvio,
  validarRegistro,
  valoresDaNovaVersao,
  valoresDoRegistro,
  type RegistroErrors,
  type RegistroValues,
} from './registroDoFormulario';

type ModoDoFormulario = 'registrar' | 'versao' | 'editar';

interface FormularioAberto {
  tipo: FlammableRecordType;
  modo: ModoDoFormulario;
  /** O registro que a linha mostrava ao abrir: o que se edita ou a versão anterior. */
  registro: FlammableRecord | null;
}

interface ErroDeLinha {
  tipo: FlammableRecordType;
  texto: string;
}

const mesmoId = (a: string | number, b: string | number): boolean => String(a) === String(b);

/** O registro mais recente do tipo, na falta de o servidor dizer qual é. */
const maisRecenteDoTipo = (registros: FlammableRecord[], tipo: FlammableRecordType): FlammableRecord | null =>
  registros
    .filter((registro) => registro.recordType === tipo)
    .reduce<FlammableRecord | null>(
      (melhor, atual) =>
        melhor === null ||
        atual.documentedAt > melhor.documentedAt ||
        (atual.documentedAt === melhor.documentedAt && Number(atual.id) > Number(melhor.id))
          ? atual
          : melhor,
      null
    );

const tituloDoFormulario = (aberto: FormularioAberto): string => {
  const rotulo = ROTULO_DO_TIPO_DE_REGISTRO[aberto.tipo];
  if (aberto.tipo === 'SIMULADO') {
    return aberto.modo === 'editar' ? 'Editar simulado de emergência' : 'Registrar simulado de emergência';
  }
  if (aberto.modo === 'versao') return `Nova versão de: ${rotulo}`;
  return `${aberto.modo === 'editar' ? 'Editar' : 'Registrar'}: ${rotulo}`;
};

const valoresIniciaisDe = (aberto: FormularioAberto): RegistroValues => {
  if (aberto.modo === 'editar' && aberto.registro) return valoresDoRegistro(aberto.registro);
  if (aberto.modo === 'versao' && aberto.registro) return valoresDaNovaVersao(aberto.registro);
  return REGISTRO_VAZIO;
};

export const StepProntuario = forwardRef<StepHandle, ListStepProps>(function StepProntuario(
  { installationId, canEdit, onDirtyChange, onSaved },
  ref
) {
  const [itens, setItens] = useState<FlammableItemSummary[]>([]);
  const [registros, setRegistros] = useState<FlammableRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const [erroDeLinha, setErroDeLinha] = useState<ErroDeLinha | null>(null);
  const [aberto, setAberto] = useState<FormularioAberto | null>(null);
  const [values, setValues] = useState<RegistroValues>(REGISTRO_VAZIO);
  const [errors, setErrors] = useState<RegistroErrors>({});
  const [arquivo, setArquivo] = useState<File | null>(null);
  const [erroDoArquivo, setErroDoArquivo] = useState<string | null>(null);
  const [erroDoFormulario, setErroDoFormulario] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [enviandoArquivo, setEnviandoArquivo] = useState<FlammableRecordType | null>(null);
  const [paraExcluir, setParaExcluir] = useState<FlammableRecord | null>(null);
  const [excluindo, setExcluindo] = useState(false);
  const [erroAoExcluir, setErroAoExcluir] = useState<string | null>(null);

  const dirty =
    aberto !== null &&
    (arquivo !== null || JSON.stringify(values) !== JSON.stringify(valoresIniciaisDe(aberto)));

  useEffect(() => {
    onDirtyChange(dirty);
  }, [dirty, onDirtyChange]);

  /** O estado de cada item e o último registro vêm do servidor; a tela só junta os dois. */
  const carregar = useCallback(async () => {
    const [instalacao, encontrados] = await Promise.all([
      flammableInstallationService.getInstallationById(installationId),
      flammableInstallationService.getRecords(installationId),
    ]);
    setItens(instalacao.summary?.items ?? []);
    setRegistros(encontrados);
  }, [installationId]);

  useEffect(() => {
    let active = true;
    setLoading(true);
    carregar()
      .catch((err: unknown) => {
        if (active) setLoadError(mensagemDoServidor(err, 'Não foi possível carregar o prontuário.'));
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [carregar]);

  const tentarDeNovo = () => {
    setLoadError(null);
    setLoading(true);
    carregar()
      .catch((err: unknown) => setLoadError(mensagemDoServidor(err, 'Não foi possível carregar o prontuário.')))
      .finally(() => setLoading(false));
  };

  const ultimoDoTipo = (tipo: FlammableRecordType): FlammableRecord | null => {
    const item = itens.find((candidato) => candidato.recordType === tipo);
    const ultimoId = item?.last?.id;
    if (ultimoId !== undefined) return registros.find((registro) => mesmoId(registro.id, ultimoId)) ?? null;
    return maisRecenteDoTipo(registros, tipo);
  };

  const abrir = (tipo: FlammableRecordType, modo: ModoDoFormulario) => {
    const novo: FormularioAberto = { tipo, modo, registro: modo === 'registrar' ? null : ultimoDoTipo(tipo) };
    setValues(valoresIniciaisDe(novo));
    setErrors({});
    setArquivo(null);
    setErroDoArquivo(null);
    setErroDoFormulario(null);
    setErroDeLinha(null);
    setAberto(novo);
  };

  const fechar = () => {
    setAberto(null);
    setValues(REGISTRO_VAZIO);
    setErrors({});
    setArquivo(null);
    setErroDoArquivo(null);
    setErroDoFormulario(null);
  };

  const alterar = (patch: Partial<RegistroValues>) => {
    setValues((anterior) => ({ ...anterior, ...patch }));
    setErrors((anterior) => {
      const proximo = { ...anterior };
      (Object.keys(patch) as Array<keyof RegistroValues>).forEach((chave) => delete proximo[chave]);
      return proximo;
    });
    setErroDoFormulario(null);
  };

  const escolherArquivo = (escolhido: File) => {
    const motivo = motivoDeRecusaDoArquivo(escolhido);
    setArquivo(motivo ? null : escolhido);
    setErroDoArquivo(motivo);
  };

  const enviar = useCallback(async (): Promise<ResultadoDoEnvio> => {
    if (aberto === null) return 'enviado';
    const encontrados = validarRegistro(aberto.tipo, values);
    setErrors(encontrados);
    if (Object.keys(encontrados).length > 0) {
      setErroDoFormulario('Corrija os campos marcados para guardar.');
      return 'incompleto';
    }
    setSaving(true);
    setErroDoFormulario(null);
    try {
      const corpo = registroParaEnvio(aberto.tipo, values);
      const guardado =
        aberto.modo === 'editar' && aberto.registro
          ? await flammableInstallationService.updateRecord(installationId, String(aberto.registro.id), corpo)
          : await flammableInstallationService.createRecord(installationId, corpo);

      let avisoDoArquivo: string | null = null;
      if (arquivo) {
        try {
          await flammableInstallationService.uploadRecordFile(installationId, String(guardado.id), arquivo);
        } catch (err: unknown) {
          avisoDoArquivo = `O registro foi guardado, mas o arquivo não subiu. ${mensagemDoServidor(err, 'Use "Anexar arquivo" para tentar de novo.')}`;
        }
      }

      fechar();
      setAviso(avisoDoArquivo);
      onSaved(aberto.tipo === 'SIMULADO' ? 'Simulado salvo.' : 'Documento salvo.');
      try {
        await carregar();
      } catch {
        setLoadError('Salvou, mas não consegui atualizar a lista de documentos.');
      }
      return 'enviado';
    } catch (err: unknown) {
      setErroDoFormulario(mensagemDoServidor(err, 'Não foi possível guardar o registro.'));
      return 'recusado';
    } finally {
      setSaving(false);
    }
  }, [aberto, arquivo, carregar, installationId, onSaved, values]);

  useImperativeHandle(
    ref,
    () => ({
      salvarPendente: async () => (dirty ? enviar() : 'enviado'),
    }),
    [dirty, enviar]
  );

  const anexar = async (tipo: FlammableRecordType, registro: FlammableRecord, escolhido: File) => {
    setErroDeLinha(null);
    const motivo = motivoDeRecusaDoArquivo(escolhido);
    if (motivo) {
      setErroDeLinha({ tipo, texto: motivo });
      return;
    }
    setEnviandoArquivo(tipo);
    try {
      await flammableInstallationService.uploadRecordFile(installationId, String(registro.id), escolhido);
      onSaved('Arquivo anexado.');
    } catch (err: unknown) {
      setErroDeLinha({ tipo, texto: mensagemDoServidor(err, 'Não foi possível anexar o arquivo.') });
      setEnviandoArquivo(null);
      return;
    }
    try {
      await carregar();
    } catch {
      setLoadError('Salvou, mas não consegui atualizar a lista de documentos.');
    } finally {
      setEnviandoArquivo(null);
    }
  };

  const baixar = async (tipo: FlammableRecordType, registro: FlammableRecord) => {
    setErroDeLinha(null);
    try {
      const blob = await flammableInstallationService.downloadRecordFile(installationId, String(registro.id));
      if (!(blob instanceof Blob)) throw new Error('Não foi possível baixar o arquivo.');
      salvarComoDownload(blob, registro.originalFileName ?? 'documento');
    } catch (err: unknown) {
      setErroDeLinha({ tipo, texto: mensagemDoServidor(err, 'Não foi possível baixar o arquivo.') });
    }
  };

  const confirmarExclusao = async () => {
    if (!paraExcluir) return;
    setExcluindo(true);
    setErroAoExcluir(null);
    try {
      await flammableInstallationService.deleteRecord(installationId, String(paraExcluir.id));
      if (aberto?.registro && mesmoId(aberto.registro.id, paraExcluir.id)) fechar();
      setParaExcluir(null);
      onSaved('Registro excluído.');
      await carregar();
    } catch (err: unknown) {
      setErroAoExcluir(mensagemDoServidor(err, 'Não foi possível excluir o registro.'));
    } finally {
      setExcluindo(false);
    }
  };

  const formularioDe = (tipo: FlammableRecordType): React.ReactNode => {
    if (!canEdit || aberto?.tipo !== tipo) return null;
    const propriedades = {
      titulo: tituloDoFormulario(aberto),
      tipo,
      values,
      errors,
      erro: erroDoFormulario,
      salvando: saving,
      arquivo,
      erroDoArquivo,
      arquivoAtual: aberto.modo === 'editar' ? (aberto.registro?.originalFileName ?? null) : null,
      onChange: alterar,
      onArquivo: escolherArquivo,
      onSubmit: () => void enviar(),
      onCancel: fechar,
    };
    return tipo === 'SIMULADO' ? <FormularioDoSimulado {...propriedades} /> : <FormularioDoDocumento {...propriedades} />;
  };

  return (
    <Box>
      <Box sx={{ maxWidth: 720, mb: 2 }}>
        <Typography variant="h6" component="h3">
          Documentos do prontuário
        </Typography>
        <Typography variant="body1" color="text.secondary">
          {canEdit
            ? 'A NR 20 exige estes seis itens para instalações de Classe I, II e III. O arquivo fica guardado aqui; o laudo lista cada um com data, responsável e onde está.'
            : 'Aqui estão os documentos do prontuário desta instalação. Você pode baixar o arquivo de cada um.'}
        </Typography>
      </Box>

      {loadError && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          action={
            <Button color="inherit" onClick={tentarDeNovo} sx={{ minHeight: 44 }}>
              Tentar de novo
            </Button>
          }
        >
          {loadError}
        </Alert>
      )}
      {aviso && (
        <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setAviso(null)}>
          {aviso}
        </Alert>
      )}

      {loading && (
        <Typography variant="body1" role="status">
          Carregando o prontuário…
        </Typography>
      )}

      {!loading && !loadError && (
        <Box component="ul" sx={{ m: 0, p: 0, borderTop: 1, borderColor: 'divider' }}>
          {FLAMMABLE_RECORD_TYPES.map((tipo) => {
            const registro = ultimoDoTipo(tipo);
            return (
              <LinhaDoProntuario
                key={tipo}
                tipo={tipo}
                item={itens.find((candidato) => candidato.recordType === tipo)}
                registro={registro}
                canEdit={canEdit}
                semAbrirFormulario={dirty && aberto?.tipo !== tipo}
                enviandoArquivo={enviandoArquivo === tipo}
                erro={erroDeLinha?.tipo === tipo ? erroDeLinha.texto : null}
                onRegistrar={() => abrir(tipo, 'registrar')}
                onNovaVersao={() => abrir(tipo, 'versao')}
                onEditar={() => abrir(tipo, 'editar')}
                onExcluir={() => {
                  setErroAoExcluir(null);
                  setParaExcluir(registro);
                }}
                onBaixar={() => registro && void baixar(tipo, registro)}
                onAnexar={(escolhido) => registro && void anexar(tipo, registro, escolhido)}
              >
                {formularioDe(tipo)}
              </LinhaDoProntuario>
            );
          })}
        </Box>
      )}

      <ConfirmDialog
        open={paraExcluir !== null}
        title="Excluir este registro?"
        message={
          paraExcluir
            ? `${ROTULO_DO_TIPO_DE_REGISTRO[paraExcluir.recordType]} de ${formatarData(paraExcluir.documentedAt)} sai do prontuário${paraExcluir.originalFileName ? ', junto com o arquivo anexado' : ''}. Não dá para desfazer.`
            : ''
        }
        confirmLabel="Excluir registro"
        loading={excluindo}
        error={erroAoExcluir}
        onConfirm={() => void confirmarExclusao()}
        onCancel={() => setParaExcluir(null)}
      />
    </Box>
  );
});

export default StepProntuario;
